import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";

const STATUS_CLASS = {
  BREACHED:  "s-fail",
  ESCALATED: "s-rev",
};

function SLABreachMonitor() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    bdcApi.adListSlaBreaches({ page, limit: 25 })
      .then((res) => {
        if (!live) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [page]);

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="SLA breach monitor"
        subtitle={<><span className="mono">{total}</span><span>breach{total === 1 ? "" : "es"}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Every investor-approval row currently in status BREACHED or ESCALATED. Click a row to open the DR detail and act.
      </p>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No active SLA breaches.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>SLA #</th>
                  <th>Investor</th>
                  <th>Type</th>
                  <th>Assigned DR</th>
                  <th>Deadline</th>
                  <th>Escalation</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const open = () => navigate(`/dr/approvals/${s.id}`);
                  const statusUpper = String(s.status || "").toUpperCase();
                  return (
                    <tr
                      key={s.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open SLA ${s.id}`}
                      onClick={open}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          open();
                        }
                      }}
                    >
                      <td className="mono">#{s.id}</td>
                      <td className="mono">#{s.investor_user_id}</td>
                      <td className="muted">{s.submission_type}</td>
                      <td className="muted">{s.assigned_dr_id ? `#${s.assigned_dr_id}` : "Unassigned"}</td>
                      <td style={{ color: "var(--neg-700)", fontWeight: 500 }}>{formatAuditTimestamp(s.deadline_at)}</td>
                      <td className="muted">Level {s.escalation_level || 0}</td>
                      <td>
                        <span className={`tbl-status ${STATUS_CLASS[statusUpper] || "s-mute"}`}>
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default SLABreachMonitor;
